import React, { FC, useMemo, useState } from "react";

import { Translate } from "@/app/application/Translate";
import { TrainingCourse } from "@/features/training/model/Training/TrainingCourse";
import {
	TrainingSession,
	TrainingSessionStatus,
} from "@/features/training/model/Training/TrainingSession";
import { CompleteCourseSession } from "@/features/training/pages/training-tab/panel-right/training-session/CompleteCourseSession";
import { EditCourseSession } from "@/features/training/pages/training-tab/panel-right/training-session/EditCourseSession";
import { CustomActionsButton } from "@/features/training/ui";
import { StyledMenu } from "@/shared/ui/StyledMenu";
import DoneIcon from "@mui/icons-material/Done";
import SchoolIcon from "@mui/icons-material/School";
import {
	Box,
	Divider,
	ListItemIcon,
	ListItemText,
	MenuItem,
	Tooltip,
} from "@mui/material";

interface CourseSessionActionsProps {
	session: TrainingSession;
	course: TrainingCourse;
}

export const CourseSessionActions: FC<CourseSessionActionsProps> = ({
	session,
	course,
}) => {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [editTrainingSessionModal, setEditTrainingSessionModal] =
		useState(false);
	const [completeTrainingSessionModal, setCompleteTrainingSessionModal] =
		useState(false);

	const open = Boolean(anchorEl);

	const isArchived = session.Status === TrainingSessionStatus.Archived;
	const isComplete = session.Status === TrainingSessionStatus.Complete;

	const canEdit = !isComplete && !isArchived;
	const canComplete = !isArchived && !isComplete;

	const handleClick = (event: React.MouseEvent<HTMLElement>) =>
		setAnchorEl(event.currentTarget);
	const handleClose = () => setAnchorEl(null);

	const openEdit = () => {
		setEditTrainingSessionModal(true);
		handleClose();
	};
	const openComplete = () => {
		setCompleteTrainingSessionModal(true);
		handleClose();
	};

	// Compact menu styling
	const paperSx = useMemo(
		() => ({
			borderRadius: 3,
			mt: 1,
			minWidth: 240,
			overflow: "hidden",
			border: "1px solid",
			borderColor: "divider",
			boxShadow:
				"0px 14px 30px rgba(0,0,0,0.12), 0px 6px 12px rgba(0,0,0,0.08)",
		}),
		[],
	);

	const itemSx = useMemo(
		() => ({
			mx: 0.75,
			my: 0.25,
			borderRadius: 2,
			px: 1,
			py: 0.75,
			minHeight: 38,
			"& .MuiListItemIcon-root": {
				minWidth: 30,
				color: "text.primary", // monochrome
			},
			"& .MuiListItemText-primary": {
				fontSize: 14.5,
				fontWeight: 500,
				letterSpacing: 0.1,
			},
			"&:hover": { backgroundColor: "action.hover" },
			"&.Mui-disabled": { opacity: 0.55 },
		}),
		[],
	);

	return (
		<>
			<Tooltip
				title={
					<Translate String="Training.Session.Actions.Tooltip">
						Edit, complete, or archive this session
					</Translate>
				}
				enterDelay={600}
			>
				<Box display="inline-flex">
					<CustomActionsButton
						open={open}
						handleClick={handleClick}
						aria-haspopup="menu"
						aria-controls={open ? "course-session-actions-menu" : undefined}
						aria-expanded={open ? "true" : undefined}
					>
						<Translate String="Training.Actions">Actions</Translate>
					</CustomActionsButton>
				</Box>
			</Tooltip>

			<StyledMenu
				id="course-session-actions-menu"
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				keepMounted
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
				PaperProps={{ sx: paperSx }}
				MenuListProps={{
					"aria-labelledby": "course-session-actions-button",
					dense: false,
					sx: { py: 0.5 },
				}}
			>
				<MenuItem sx={itemSx} onClick={openEdit}>
					<ListItemIcon>
						<SchoolIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={
							<Translate
								HTMLTag="span"
								String="Training.Session.Actions.EditTrainingSession"
							>
								{canEdit ? "Edit training session" : "View training session"}
							</Translate>
						}
					/>
				</MenuItem>

				<Divider sx={{ my: 0.5, mx: 1.5 }} />

				<MenuItem sx={itemSx} onClick={openComplete} disabled={!canComplete}>
					<ListItemIcon>
						<DoneIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={
							<Translate
								HTMLTag="span"
								String="Training.Session.Actions.Complete"
							>
								Mark as complete
							</Translate>
						}
					/>
				</MenuItem>
			</StyledMenu>

			{/* Dialogs */}
			<EditCourseSession
				open={editTrainingSessionModal}
				handleClose={() => setEditTrainingSessionModal(false)}
				session={session}
				course={course}
				readOnly={isComplete || isArchived}
			/>
			<CompleteCourseSession
				open={completeTrainingSessionModal}
				handleClose={() => setCompleteTrainingSessionModal(false)}
				id={session.Id}
				students={session.Students}
			/>
		</>
	);
};
